
"use client";

import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Search, Plus, ArrowUp, ArrowDown, Pencil, Trash, PlusCircle } from "lucide-react";
import AddCustomerModal from '../components/AddCustomerModal';
import EditCustomerModal from '../components/EditCustomerModal';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function Customers() {
  const { storeId } = useParams(); // Get storeId from URL
  const { user } = useAuth(); // Get user for storeIds validation
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [typeFilter, setTypeFilter] = useState("all");
  const [balanceFilter, setBalanceFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddPartyModal, setShowAddPartyModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [transactionError, setTransactionError] = useState("");
  const [newTransaction, setNewTransaction] = useState({
    date: new Date().toISOString().split("T")[0],
    customerId: "",
    partyName: "",
    type: "credit",
    amount: "",
    description: "",
    paymentMode: "Cash",
  });
  const navigate = useNavigate();

  useEffect(() => {
    // Validate storeId
    if (!storeId || !user?.storeIds.includes(parseInt(storeId))) {
      console.log('Invalid storeId or unauthorized:', { storeId, userStoreIds: user?.storeIds });
      navigate('/unauthorized');
      return;
    }
    fetchCustomers();
    // eslint-disable-next-line
  }, [page, size, search, sortBy, sortDir, storeId, user, navigate]);

  const fetchCustomers = async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem('token');
      console.log('Fetching customers with token:', token ? 'Token present' : 'No token');
      const res = await api.get(`/customers/by-store/${storeId}?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}&search=${search}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });
      console.log('Customers response:', res.data);
      const mappedCustomers = res.data.data?.content?.map(customer => ({
        ...customer,
        type: customer.type || "customer",
        email: customer.email || "",
        lastTransaction: customer.lastTransaction || null,
        balance: customer.currentBalance ?? 0,
      })) || mockCustomers;
      console.log('Mapped customers:', mappedCustomers);
      setCustomers(mappedCustomers);
      setTotalPages(res.data.data?.totalPages || 1);
    } catch (err) {
      console.error('Fetch customers error:', err);
      if (err.response?.status === 401) {
        console.log('Unauthorized - redirecting to login');
        navigate('/login');
      }
      setError(err.response?.data?.message || err.message || 'Failed to fetch customers');
      setCustomers(mockCustomers.map(customer => ({
        ...customer,
        balance: customer.currentBalance ?? 0,
      })));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/customers/${id}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });
      setCustomers(customers.filter(p => p.id !== id));
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to delete customer');
    }
  };

  const handleEditCustomer = (updatedCustomer) => {
    setCustomers(customers.map(p => p.id === updatedCustomer.id ? {
      ...updatedCustomer,
      type: updatedCustomer.type || "customer",
      email: updatedCustomer.email || "",
      lastTransaction: updatedCustomer.lastTransaction || null,
      balance: updatedCustomer.currentBalance ?? 0,
    } : p));
    setShowEditModal(false);
    setSelectedCustomer(null);
  };

  // NEW (only updates state, no extra API call)
  const handleAddParty = (newCustomer) => {
    const customer = {
      ...newCustomer,
      type: newCustomer.type || "customer",
      email: newCustomer.email || "",
      lastTransaction: newCustomer.lastTransaction || null,
      balance: newCustomer.currentBalance ?? 0,
      isActive: newCustomer.isActive ?? true,
    };
    setCustomers((prev) => [...prev, customer]);
    setShowAddPartyModal(false);
  };
2121

  const handleImport = async () => {
    setImporting(true);
    try {
      const token = localStorage.getItem('token');
      await api.post(`/customers/${storeId}/import`, {}, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });
      fetchCustomers();
      alert('Import successful!');
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to import customers');
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/customers/${storeId}/export`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `customers_store_${storeId}_export.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      alert('Export successful!');
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to export customers');
    } finally {
      setExporting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTransaction({
      ...newTransaction,
      [name]: name === "amount" ? (value === "" ? "" : Number(value)) : value,
    });
  };

  const handleAddTransaction = async () => {
    if (!selectedCustomer?.id) {
      setTransactionError("Please select a customer");
      return;
    }
    if (!newTransaction.amount || newTransaction.amount <= 0) {
      setTransactionError("Amount must be greater than 0");
      return;
    }
    if (!newTransaction.description.trim()) {
      setTransactionError("Description is required");
      return;
    }
    if (!newTransaction.date) {
      setTransactionError("Date is required");
      return;
    }

    setTransactionError("");
    try {
      const token = localStorage.getItem('token');
      const payload = {
        customerId: selectedCustomer.id,
        type: newTransaction.type === "credit" ? "PAYMENT_RECEIVED" : "PAYMENT_GIVEN",
        amount: Math.abs(newTransaction.amount),
        transactionDate: newTransaction.date,
        mode: newTransaction.paymentMode.toUpperCase().replace(" ", "_"),
        category: newTransaction.description,
        notes: newTransaction.description,
        receiptNumber: `TRX-${Date.now()}`,
      };
      console.log('Transaction payload:', payload);
      console.log('Current balance before transaction:', selectedCustomer.balance);
      const res = await api.post('/transactions/payment', payload, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });
      console.log('Transaction response:', res.data);
      await fetchCustomers();
      setShowAddModal(false);
      setNewTransaction({
        date: new Date().toISOString().split("T")[0],
        customerId: "",
        partyName: "",
        type: "credit",
        amount: "",
        description: "",
        paymentMode: "Cash",
      });
      setSelectedCustomer(null);
      alert("Transaction added successfully!");
    } catch (err) {
      console.error('Transaction error:', err);
      setTransactionError(err.response?.data?.message || err.message || 'Failed to add transaction');
    }
  };

  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch = customer.name?.toLowerCase().includes(search.toLowerCase()) || false;
    const matchesType = typeFilter === "all" || customer.type === typeFilter;
    const matchesBalance = balanceFilter === "all" ||
      ((customer.balance ?? 0) > 0 && balanceFilter === "positive") ||
      ((customer.balance ?? 0) < 0 && balanceFilter === "negative") ||
      ((customer.balance ?? 0) === 0 && balanceFilter === "zero");
    return matchesSearch && matchesType && matchesBalance;
  });

  const totalReceivable = filteredCustomers
    .filter((customer) => (customer.balance ?? 0) > 0)
    .reduce((sum, customer) => sum + (customer.balance ?? 0), 0);

  const totalPayable = filteredCustomers
    .filter((customer) => (customer.balance ?? 0) < 0)
    .reduce((sum, customer) => sum + Math.abs(customer.balance ?? 0), 0);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Customers</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAddPartyModal(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            <Plus className="h-4 w-4 mr-2 inline" />
            Add Customers
          </button>
          <button
            onClick={handleImport}
            disabled={importing}
            className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
          >
            {importing ? 'Importing...' : 'Import'}
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
          >
            {exporting ? 'Exporting...' : 'Export'}
          </button>
        </div>
      </div>

      <div className="mb-4 flex flex-col md:flex-row gap-4 items-start md:items-center">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search customers..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="pl-10 block w-full md:w-64 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        >
          <option value="all">All Types</option>
          <option value="customer">Customers</option>
        </select>

        <select
          value={balanceFilter}
          onChange={(e) => setBalanceFilter(e.target.value)}
          className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        >
          <option value="all">All Balances</option>
          <option value="positive">Receivable</option>
          <option value="negative">Payable</option>
          <option value="zero">Zero Balance</option>
        </select>

        <select
          value={sortBy}
          onChange={(e) => { setSortBy(e.target.value); setPage(0); }}
          className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        >
          <option value="name">Sort by Name</option>
          <option value="currentBalance">Sort by Balance</option>
          <option value="creditLimit">Sort by Credit Limit</option>
        </select>

        <select
          value={sortDir}
          onChange={(e) => { setSortDir(e.target.value); setPage(0); }}
          className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        >
          <option value="asc">Ascending</option>
          <option value="desc">Descending</option>
        </select>
      </div>

      {error && <div className="mb-4 text-red-600">{error}</div>}
      {transactionError && <div className="mb-4 text-red-600">{transactionError}</div>}
      {loading ? <div className="text-gray-600">Loading...</div> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Customers</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{filteredCustomers.length}</p>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between text-sm">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Customers: </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {filteredCustomers.filter((customer) => customer.type === "customer").length}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Suppliers: </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {filteredCustomers.filter((customer) => customer.type === "supplier").length}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Receivable</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    ₹{totalReceivable.toLocaleString()}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <ArrowDown className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
              <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                From {filteredCustomers.filter((customer) => (customer.balance ?? 0) > 0).length} customers
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Due Amount</p>
                  <p className="text-2xl font-bold text-red-600 dark:text-red-400">₹{totalPayable.toLocaleString()}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <ArrowUp className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                From {filteredCustomers.filter((customer) => (customer.balance ?? 0) < 0).length} Customer
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Balance</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Last Transaction</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredCustomers.map((customer) => (
                    <tr key={customer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td
                        className="px-6 py-4 whitespace-nowrap cursor-pointer"
                        onClick={() => {
                          setSelectedCustomer(customer);
                          setNewTransaction({ ...newTransaction, customerId: customer.id, partyName: customer.name || 'Unknown' });
                          setShowAddModal(true);
                        }}
                      >
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-300">{customer.name || 'Unknown'}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{customer.address || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${customer.type === 'customer' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                          {(customer.type || 'customer').charAt(0).toUpperCase() + (customer.type || 'customer').slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-300">{customer.phone || '-'}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{customer.email || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                        <span className={`font-medium ${(customer.balance ?? 0) > 0 ? 'text-green-600' : (customer.balance ?? 0) < 0 ? 'text-red-600' : 'text-gray-900 dark:text-gray-300'
                          }`}>
                          ₹{(customer.balance ?? 0).toLocaleString('en-IN')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                        {customer.lastTransaction ? new Date(customer.lastTransaction).toLocaleDateString('en-IN') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                        <button
                          className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 mr-3"
                          onClick={() => { setSelectedCustomer(customer); setShowEditModal(true); }}
                        >
                          <Pencil className="h-5 w-5" />
                        </button>
                        <button
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 mr-3"
                          onClick={() => { setSelectedCustomer(customer); setShowDeleteModal(true); }}
                        >
                          <Trash className="h-5 w-5" />
                        </button>
                        <button
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setNewTransaction({ ...newTransaction, customerId: customer.id, partyName: customer.name || 'Unknown' });
                            setShowAddModal(true);
                          }}
                        >
                          <PlusCircle className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-center mt-4 gap-2">
            <button
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Prev
            </button>
            <span>Page {page + 1} of {totalPages}</span>
            <button
              disabled={page + 1 >= totalPages}
              onClick={() => setPage(page + 1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </>
      )}

      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">

            </span>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Add New Transaction</h3>
                    <div className="mt-4 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Date
                          </label>
                          <input
                            type="date"
                            name="date"
                            id="date"
                            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            value={newTransaction.date}
                            onChange={handleInputChange}
                            required
                          />
                        </div>

                        <div>
                          <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Type
                          </label>
                          <select
                            name="type"
                            id="type"
                            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            value={newTransaction.type}
                            onChange={handleInputChange}
                          >
                            <option value="credit">Credit (Receive)</option>
                            <option value="debit">Debit (Pay)</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="partyName"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Party Name
                        </label>
                        <input
                          type="text"
                          name="partyName"
                          id="partyName"
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                          value={newTransaction.partyName}
                          disabled
                        />
                      </div>

                      <div>
                        <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Amount
                        </label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 dark:text-gray-400 sm:text-sm">₹</span>
                          </div>
                          <input
                            type="number"
                            name="amount"
                            id="amount"
                            className="block w-full pl-7 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            value={newTransaction.amount}
                            onChange={handleInputChange}
                            min="0.01"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="description"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Description
                        </label>
                        <input
                          type="text"
                          name="description"
                          id="description"
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                          value={newTransaction.description}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="paymentMode"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Payment Mode
                        </label>
                        <select
                          name="paymentMode"
                          id="paymentMode"
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                          value={newTransaction.paymentMode}
                          onChange={handleInputChange}
                        >
                          <option value="Cash">Cash</option>
                          <option value="UPI">UPI</option>
                          <option value="Bank Transfer">Bank Transfer</option>
                          <option value="Cheque">Cheque</option>
                          <option value="Credit Card">Credit Card</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleAddTransaction}
                >
                  Add Transaction
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => { setShowAddModal(false); setSelectedCustomer(null); setTransactionError(""); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && selectedCustomer && (
        <EditCustomerModal
          open={showEditModal}
          onClose={() => { setShowEditModal(false); setSelectedCustomer(null); }}
          customer={selectedCustomer}
          onEditCustomer={handleEditCustomer}
        />
      )}

      {showDeleteModal && selectedCustomer && (
        <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true"></span>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg
                      className="h-6 w-6 text-red-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">
                      Delete Customer
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete {selectedCustomer?.name || 'Unknown'}? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => {
                    handleDelete(selectedCustomer.id);
                    setShowDeleteModal(false);
                    setSelectedCustomer(null);
                  }}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedCustomer(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddPartyModal && (
        <AddCustomerModal
          open={showAddPartyModal}
          onClose={() => setShowAddPartyModal(false)}
          storeId={storeId}
          onAddParty={handleAddParty} // will receive created customer
        />

      )}
    </div>
  );
}